import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import { RootState } from '..';

interface CustomerInfo {
  email: string;
  phone?: string;
  shippingAddress?: any;
  billingAddress?: any;
}

interface OrderDetails {
  sessionId?: string;
  orderId?: string;
  orderDate?: string;
  orderStatus?: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled';
  trackingNumber?: string;
}

interface CheckoutState {
  isCheckoutOpen: boolean;
  checkoutUrl: string | null;
  customerInfo: CustomerInfo;
  orderDetails: OrderDetails;
  orderHistory: OrderDetails[];
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: CheckoutState = {
  isCheckoutOpen: false,
  checkoutUrl: null,
  customerInfo: {
    email: '',
  },
  orderDetails: {},
  orderHistory: [],
  status: 'idle',
  error: null
};

// Async thunk for initiating checkout
export const initiateCheckout = createAsyncThunk(
  'checkout/initiateCheckout',
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState() as RootState;
      const { designName, size, quantity, finish, canvasShape, backgroundColor } = state.design;
      
      // Get the canvas data URL
      const canvas = state.design.canvas;
      if (!canvas) {
        return rejectWithValue('Canvas not initialized');
      }
      
      const dataUrl = canvas.toDataURL({
        format: 'png',
        quality: 1
      });
      
      // Calculate price (this is a simplified example)
      const basePrice = 5.99;
      const sizeMultiplier = size.width * size.height / 9; // Normalized by 3x3 inches
      const quantityDiscount = quantity > 100 ? 0.8 : (quantity > 50 ? 0.9 : 1);
      const price = basePrice * sizeMultiplier * quantityDiscount;
      
      const response = await fetch('/api/checkout', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: [{
            name: designName,
            price,
            quantity,
            size: `${size.width}x${size.height}`,
            shape: canvasShape,
            finish,
            image_url: dataUrl,
            customer_email: state.checkout.customerInfo.email,
            customer_phone: state.checkout.customerInfo.phone,
            shipping_address: state.checkout.customerInfo.shippingAddress,
            billing_address: state.checkout.customerInfo.billingAddress,
          }],
          origin: window.location.origin
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.error || 'Checkout failed');
      }
      
      const data = await response.json();
      return data.url;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Checkout failed');
    }
  }
);

// Async thunk for fetching order details
export const fetchOrderDetails = createAsyncThunk(
  'checkout/fetchOrderDetails',
  async (sessionId: string, { rejectWithValue }) => {
    try {
      const response = await fetch(`/api/order-details?session_id=${sessionId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        return rejectWithValue(errorData.error || 'Failed to fetch order details');
      }
      
      const data = await response.json();
      return data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch order details');
    }
  }
);

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState,
  reducers: {
    setCheckoutOpen: (state, action: PayloadAction<boolean>) => {
      state.isCheckoutOpen = action.payload;
    },
    setCustomerInfo: (state, action: PayloadAction<Partial<CustomerInfo>>) => {
      state.customerInfo = { ...state.customerInfo, ...action.payload };
    },
    resetCheckout: (state) => {
      state.isCheckoutOpen = false;
      state.checkoutUrl = null;
      state.status = 'idle';
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(initiateCheckout.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(initiateCheckout.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.checkoutUrl = action.payload;
      })
      .addCase(initiateCheckout.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      })
      .addCase(fetchOrderDetails.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchOrderDetails.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.orderDetails = {
          sessionId: action.meta.arg,
          orderId: action.payload.orderId,
          orderDate: new Date().toISOString(),
          orderStatus: 'processing',
          ...action.payload
        };
        state.orderHistory.push(state.orderDetails);
      })
      .addCase(fetchOrderDetails.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload as string;
      });
  }
});

export const {
  setCheckoutOpen,
  setCustomerInfo,
  resetCheckout
} = checkoutSlice.actions;

export default checkoutSlice.reducer;
